import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";
import { rollbackCart } from "./userSlice";

const usersAdapter = createEntityAdapter({});
const initialState = usersAdapter.getInitialState();

export const usersApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
      getUsers: builder.query({
          query: () => '/user',
          validateStatus: (response, result) => {
              return response.status === 200 && !result.isError
          },
          transformResponse: responseData => {
              const loadedUsers = responseData.map(user => {
                  user.id = user._id
                  return user
              });
              return usersAdapter.setAll(initialState, loadedUsers)
          },
          providesTags: (result, error, arg) => {
              if (result?.ids) {
                  return [
                      { type: 'User', id: 'LIST' },
                      ...result.ids.map(id => ({ type: 'User', id }))
                  ]
              } else return [{ type: 'User', id: 'LIST' }]
          }
      }),
      //   getUserById: builder.query({
      //     query: (id) => `/user/getUserById?id=${id}`,
      //   }),
      getUserById: builder.query({
        query: id => `/user/getUserById?id=${id}`,
        providesTags: (result, error, id) => [{ type: 'User', id }]
      }),
      getUserByIdV: builder.query({
        query: id => `/user/getUserByIdV?id=${id}`,
        providesTags: (result, error, id) => [{ type: 'User', id }]
      }),
      addNewUser: builder.mutation({
        query: data => ({
            url: '/user/register',
            method: 'POST',
            body: data,
        }),
        // invalidatesTags: ["Product"]
        onQueryStarted: async (newUser, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            usersApiSlice.util.updateQueryData('getUsers', undefined, draft => {
              usersAdapter.addOne(draft, newUser);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      }),
      addToCart: builder.mutation({
        query: data => ({
            url: '/user/addToCart',
            method: 'POST',
            body: data,
        }),
        async onQueryStarted({ productId, userId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (error) {
            // If the backend operation fails, we need to rollback the optimistic update
            dispatch(rollbackCart());
            // You might want to show an error message to the user here
            console.error('Failed to add to cart:', error);
          }
        }
      }),
      removeFromCart: builder.mutation({
        query: data => ({
            url: '/user/removeFromCart',
            method: 'POST',
            body: data,
        }),
        async onQueryStarted({ productId, userId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (error) {
            // If the backend operation fails, we need to rollback the optimistic update
            dispatch(rollbackCart());
            // You might want to show an error message to the user here
            console.error('Failed to remove from cart:', error);
          }
        }
      }),
      editPicture: builder.mutation({
        query: data => ({
          url: "/user/editPicture",
          method: "PATCH",
          body: data,
        }),
        invalidatesTags: ["User"]
      }),
      updateUser: builder.mutation({
        query: initialUserData => ({
            url: '/user/edit',
            method: 'PATCH',
            body: {
                ...initialUserData,
            }
        }),
        // invalidatesTags: (result, error, arg) => [
        //     { type: 'User', id: arg.id }
        // ]
        invalidatesTags: (result, error, { id }) => [{ type: 'User', id }]
      }),
      changePassword: builder.mutation({
        query: data => ({
            url: '/user/editPassword',
            method: 'PATCH',
            body: data
        }),
        invalidatesTags: (result, error, { id }) => [{ type: 'User', id }]
      }),
      deleteUser: builder.mutation({
        query: ({ id }) => ({
            url: `/user/delete`,
            method: 'DELETE',
            body: { id }
        }),
        // invalidatesTags: (result, error, arg) => [
        //     { type: 'User', id: arg.id }
        // ]
        invalidatesTags: (result, error, { id }) => [{ type: 'User', id }]
      }),
  }),
})

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetUserByIdVQuery,
  useAddNewUserMutation,
  useAddToCartMutation,
  useRemoveFromCartMutation,
  useEditPictureMutation,
  useUpdateUserMutation,
  useChangePasswordMutation,
  useDeleteUserMutation,
} = usersApiSlice

// returns the query result object
export const selectUsersResult = usersApiSlice.endpoints.getUsers.select()

// creates memoized selector
const selectUsersData = createSelector(
  selectUsersResult,
  usersResult => usersResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
  selectAll: selectAllUsers,
  selectById: selectUserById,
  selectIds: selectUserIds
  // Pass in a selector that returns the products slice of state
} = usersAdapter.getSelectors(state => selectUsersData(state) ?? initialState)